import { AbstractControl } from '@angular/forms';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { InputRequirements, PasswordRequirementsService } from 'app/shared/services/password-requirements.service';
import { InputTypes } from 'app/shared/enums/input-types.enum';
import { tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';



@UntilDestroy()
@Component({
  selector: 'kh-password-requirements',
  templateUrl: './password-requirements.component.html',
  styleUrls: ['./password-requirements.component.less'],
  standalone: false
})
export class PasswordRequirementsComponent {

  @Input() passwordFormControl!: AbstractControl;
  @Input() matchingFormControl!: AbstractControl;
  @Output() pwdMatchEvent: EventEmitter<boolean> = new EventEmitter();
  @Output() pwdComplexEvent: EventEmitter<boolean> = new EventEmitter();

  public inputTypes = InputTypes;
  public markAsError = false;
  public showRequired = false;
  public requirementMessages!:{ [key in InputRequirements]: string };
  private candidatePassword = '';

  public constructor(private readonly passwordRequirementsService: PasswordRequirementsService) { }

  public ngAfterViewInit() {
    this.requirementMessages = this.passwordRequirementsService.getAllRequirementMessages();
    this.subscribeToFirstPasswordUpdates()
    this.subscribeToPasswordMatcherUpdates();
  }

  private subscribeToFirstPasswordUpdates(): void {
    this.passwordFormControl.valueChanges.pipe(
      tap(pwd => this.validateCandidatePassword(pwd)),
      tap(() => this.pwdComplexEvent.emit(this.isPasswordComplex())),
      untilDestroyed(this)
    ).subscribe();
  }

  private subscribeToPasswordMatcherUpdates(): void {
    if (this.matchingFormControl) {
      this.matchingFormControl.valueChanges.pipe(
        tap(secondPwd => this.updatePasswordMatchValidation(secondPwd)),
        untilDestroyed(this)
      ).subscribe();
    }
  }

  private validateCandidatePassword(password: string): void {
    if (password && password.length > 0) {
      this.candidatePassword = password;
      this.validatePassword();
    }
    else {
      this.candidatePassword = '';
      this.passwordRequirementsService.resetAllRequirements();
    }
  }

  private updatePasswordMatchValidation(secondPwd: string): void {
    if (this.candidatePassword === secondPwd) {
      this.pwdMatchEvent.emit(true);
    } else {
      this.pwdMatchEvent.emit(false);
    }
  }

  public getRequirementMessage(requirement: InputRequirements): string {
    return this.passwordRequirementsService.getSingleRequirementMessage(requirement);
  }

  public getRequirementKeys(): Array<InputRequirements> {
    return this.passwordRequirementsService.getAllRequirementKeys();
  }

  public isPasswordComplex(): boolean {
    if (!this.getInputSoFar()) {
      this.showRequired = true;

      return false;
    }
    this.showRequired = false;
    this.markAsError = true;
    this.validatePassword();

    return this.passwordRequirementsService.isAllRequirementsMet();
  }

  public isRequirementUnmetMessageShowable(key: InputRequirements): boolean {
    return !this.isRequirementMet(key) && this.markAsError;
  }

  public isRequirementMet(requirement: InputRequirements): boolean {
    return this.passwordRequirementsService.isRequirementMet(requirement) || false;
  }

  public showRequirements(): boolean {
    return this.getInputSoFar().length > 0;
  }

  public getRequirementMessageByKey(key: InputRequirements): string {
    return this.requirementMessages[key];
  }

  private validatePassword() {
    this.showRequired = false;
    this.passwordRequirementsService.updateCandidate(this.getInputSoFar());
  }

  public getInputSoFar(): string {
    return (this.candidatePassword) ? this.candidatePassword as string : '';
  }
}
